import { Grid, makeStyles, Card, CardContent, Paper, useMediaQuery, AppBar, Toolbar, Typography, IconButton } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { requiresLocationConfirmation, requiresNewLocation } from 'util/bookingStates';
import { LocationConfirmDialog } from '../LocationConfirmation/ConfirmDialog';
import { ScheduleConfirmationDialog } from '../ScheduleConfimation/ConfirmDialog';
import { bookingState, isWaitingForPollState, manualRefreshState, requiresNewScheduleState } from '../RecoilState';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { BookingDetailCard } from '../Details/BookingDetail/BookingDetailCard';
import ReactJson from 'react-json-view';
import { LocationEdit } from '../LocationConfirmation/LocationEdit';
import { useTrackEvent, useTrackPage } from 'util/trackEvent';
import locationsService from 'services/locations.service';
import useCustomModal from 'components/CustomConfirm';
import { InvalidLocationConfirm } from '../LocationConfirmation/InvalidLocationConfirm';
import { SUPPORT_CHAT, openChatState } from '..';
import { ArrowBack } from '@material-ui/icons';
import CustomConfirmDialog from 'components/CustomConfirm/CustomConfirmDialog';
import { formatAddressObject } from 'util/format';
import { useStateRef } from '@zennya/web-component-library/src';
import { MapBox } from 'components/AddressLocationForm/MapBox';
import { getServicableAreas } from 'services/service.service';

const useStyles = makeStyles(theme => ({
  map: {
    width: 'auto',
    height: '25vh'
  },
}));
const BookingConfirm = ({ }) => {
  const classes = useStyles()
  const history = useHistory()
  const browserlocation = useLocation()
  const confirm = useCustomModal()
  const invalidConfirm = useCustomModal()
  const booking = useRecoilValue(bookingState);
  const [loading, setLoading] = useState(false);

  const setChatOpen = useSetRecoilState(openChatState);

  const [editing, setEditing] = useState(false);

  const [dirty, setDirty, dirtyRef] = useStateRef(false);

  const [locationUpdated, setLocationUpdated] = useState(false);
  const requiresNewSchedule = useRecoilValue(requiresNewScheduleState)
  const [updatedAddress, setUpdatedAddress] = useState();


  const trackEvent = useTrackEvent()
  const trackPage = useTrackPage()
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"), {
    noSsr: true,
  });

  const setManualRefreshState = useSetRecoilState(manualRefreshState);
  const [isWaitingForPoll, setIsWaitingForPoll] = useRecoilState(isWaitingForPollState);

  const showConfirmDialog = useCallback(
    (loc) => {
      confirm(props => {
        return (
          <CustomConfirmDialog {...props}
            description={
              <>
                <MapBox
                  draggable={false}
                  center={{ latitude: loc?.latitude, longitude: loc?.longitude }}
                  markerLocation={{ latitude: loc?.latitude, longitude: loc?.longitude }}
                  onMarkerDragEnd={(lnglat) => { }}
                // onRef={(ref) => {
                //   mapRef.current = ref
                // }}
                />
                <Typography>
                  {formatAddressObject(loc)}
                </Typography>
              </>
            }
            title={'Provider will arrive at this location'}
            confirmationText='Confirm'
            cancellationText='Change address'
            onConfirm={async () => {
              trackEvent('Set new location confirmation - OK', { dialog: 'newLocationConfirmProceed', })
              props.onClose()
              await updateAndOrConfirm(loc)
            }}
            onCancel={() => {
              trackEvent('Set new location confirmation - Cancel', { dialog: 'newLocationConfirmProceed' })
              props.onClose()
            }}
          />
        )
      })
    },
    [updatedAddress],
  );

  const showInvalidLocation = useCallback(
    async (error, locationId, locationProps) => {
      let description = null
      if (error?.response?.data?.code === 'INVALID_LOCATION') {
        const serviceableAreas = await getServicableAreas()
        description = <>
          <h3>Your selected location is not within the servicable area</h3>
          <img src={serviceableAreas?.map} className={classes.map} />
          <Grid container spacing={2}>
            {serviceableAreas?.cities?.map((city, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <Typography variant="body2" color="textSecondary" className={classes.city}>
                  {city}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </>
      }
      invalidConfirm(dialogprops => {
        return (
          <InvalidLocationConfirm
            {...dialogprops}
            locationId={locationId}
            updatedLocation={locationProps}
            trackEvent={trackEvent}
            confirm={confirm}
            error={error?.response?.data}
            onOpenSupport={() => {
              setChatOpen(SUPPORT_CHAT)
              dialogprops.onClose()
            }}
            onClose={() => {
              dialogprops.onClose()
            }}
            onSelectEdit={() => {
              dialogprops.onClose()
            }}
            description={description}
            booking={booking} />
        )
      })
    },
    [booking],
  );
  const updateAndOrConfirm = useCallback(
    async (props) => {
      const locationId = props?.locationId
      try {
        setLoading(true);
        setIsWaitingForPoll(true)
        let location = null;
        if (dirtyRef.current) {
          if (locationId) {
            await locationsService.setWhitelabelUserLocationById(booking?.id, locationId)
          } else {
            await locationsService.updateWhitelabelBookingLocation(booking?.id, props)
          }
        } else {
          if (locationId) {
            await locationsService.setWhitelabelUserLocationById(booking?.id, locationId)
          } else if (requiresNewLocation(booking)) {
            await locationsService.addWhitelabelBookingLocation(booking?.id, props)
          }
        }
        const res = await locationsService.confirmWhitelabelUserLocation(booking?.id)
        setManualRefreshState(Math.random())
        setLoading(false);
        history.replace({
          pathname: `/bookings/${booking?.id}`,
          search: browserlocation.search,
        })

      } catch (error) {
        showInvalidLocation(error, locationId, props)
        setLoading(false);
        setIsWaitingForPoll(false)
      }
    },
    [dirtyRef.current, booking, showInvalidLocation],
  );

  const renderLocationForm = useCallback(
    () => {
      return (
        <LocationEdit
          affirmLabel={requiresNewLocation(booking) ? 'Confirm Location' : 'Update Location'}
          loading={loading}
          isEditing={true}
          location={booking?.user_location || {
            latitude: booking?.regional_office?.latitude,
            longitude: booking?.regional_office?.longitude,
            newLocation: true
          }}
          requiresNewLocation={requiresNewLocation(booking)}
          forceRequireNewLocation={requiresNewLocation(booking)}
          onSelectConfirm={(v) => {
            updateAndOrConfirm()

          }}
          onSelectExistingConfirm={(v) => {
            setDirty(true)
            updateAndOrConfirm({ locationId: v })
          }}
          onSelectEdit={(v) => {
            setEditing(v)
          }}
          onUpdateAddress={(v) => {
            const { newLocation, dirty: isDirty, ...rest } = v
            setUpdatedAddress(rest)
            setDirty(isDirty)
            showConfirmDialog(v)
            // setForceRequireNewLocation(false)
          }}

        />
      )
    }, [booking]
  );
  const renderConfirmDialogs = useMemo(
    () => {
      if (requiresLocationConfirmation(booking) && requiresNewSchedule) {
        return renderLocationForm()
      } else if (requiresLocationConfirmation(booking)) {
        return renderLocationForm()
        // return <LocationConfirmDialog
        //   asDialog={false}
        //   forceRequireNewLocation={requiresNewLocation(booking)}
        //   booking={booking}
        //   confirmContext={confirm}
        // />
      } else if (requiresNewSchedule) {
        return <ScheduleConfirmationDialog
          asDialog={false}
          booking={booking}
          confirmContext={confirm}
        />
      } else {
        return <Redirect to={{
          pathname: `/bookings/${booking.id}`,
          search: history.location.search
        }} />
      }
    }, [booking]
  );

  return (
    <>
      <AppBar elevation={0} position="static" color="secondary">
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => {
            history.goBack()
          }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" className={classes.title}>
            {requiresNewLocation(booking) ? 'Use saved ' : 'Edit'} location
          </Typography>

        </Toolbar>
      </AppBar>

      <Paper>
        <CardContent>
          {renderConfirmDialogs}
        </CardContent>
      </Paper>
    </>
  )
}

export { BookingConfirm }